import { formatUnits, parseUnits } from "viem";
import { environment } from "src/environments/environment";

export class ContractUtils {

  /**
   * Decimals multipler
   * @param [multipler]
   * @param [value]
   * @returns
   */
  decimalMultipler(multipler = 1, value = 0) {
    let decimalValue: any = 0;
    try {
      switch (multipler) {
        case 18:
        case 9:
        case 6:
          decimalValue = parseUnits(value.toString(), multipler).toString();
          break;
        default:
          decimalValue = value / Math.pow(10, multipler);
          break;
      }
      return decimalValue;
    }
    catch (e) {
      console.log('exception...', e);
    }
    finally {
      return decimalValue;
    }
  }

  /**
   * Accept fee in wei, matching the SyndicatePool's own arithmetic.
   *
   * The pool derives each side's fee on its own as `amount * basisPoints / 10_000` and
   * adds the two together, then requires the value passed in to equal that exactly.
   * Doing the same sum in floats loses the last few wei — (10 * 1) / 100 + (10 * 2) / 100
   * is 0.30000000000000004, not 0.3 — so the deposit is rejected. Keeping the whole
   * calculation in BigInt, and flooring each side separately before adding, reproduces
   * the on-chain figure to the wei.
   *
   * @param amountWei deposit principal already scaled to token wei
   * @param lenderFeePercent lender accept fee as a percent (1.5 means 1.5%)
   * @param borrowerFeePercent borrower accept fee as a percent; only charged on refinance pools
   * @returns the total accept fee as a wei string
   */
  acceptFeeWei(
    amountWei: string | number | bigint = '0',
    lenderFeePercent: number = 0,
    borrowerFeePercent: number = 0
  ): string {
    try {
      const wei = BigInt(typeof amountWei === 'number' ? Math.trunc(amountWei) : amountWei);
      if (wei <= 0n) return '0';
      // The contract holds fee rates in basis points; the UI holds them as percents.
      const toBasisPoints = (percent: number) => BigInt(Math.round(Number(percent || 0) * 100));
      const lenderFee = (wei * toBasisPoints(lenderFeePercent)) / 10_000n;
      const borrowerFee = (wei * toBasisPoints(borrowerFeePercent)) / 10_000n;
      return (lenderFee + borrowerFee).toString();
    } catch (e) {
      return '0';
    }
  }

  /**
   * Rounds a wei token amount UP to the next whole token. Used for ERC20
   * allowance approvals so the approved amount always clears what the contract
   * pulls in transferFrom, with whole-token headroom over any sub-unit drift.
   * Only ever apply this to the ALLOWANCE amount — never to a payment amount,
   * since the contract pulls the exact figure and ceiling a payment would overpay.
   * @param amountWei amount already scaled to token wei (e.g. from decimalMultipler)
   * @param decimals token decimals
   * @returns the ceiled amount as a wei string
   */
  ceilToWholeToken(amountWei: string | number | bigint = '0', decimals = 18): string {
    try {
      const wei = BigInt(typeof amountWei === 'number' ? Math.trunc(amountWei) : amountWei);
      if (wei <= 0n) return wei.toString();
      // Mirror getDecimal's env-aware fallback (prod = 6-dp USDC, dev/staging = 18-dp)
      // for the rare case an invalid decimals value reaches here.
      const safeDecimals = Number.isInteger(Number(decimals)) && Number(decimals) >= 0
        ? Number(decimals)
        : (environment.production ? 6 : 18);
      const unit = 10n ** BigInt(safeDecimals);
      const ceiled = ((wei + unit - 1n) / unit) * unit;
      return ceiled.toString();
    } catch (e) {
      return amountWei?.toString?.() ?? '0';
    }
  }

  /**
   * Decimals divider
   * @param [divider]
   * @param [value]
   * @returns
   */
  decimalDivider(divider = 1, value: string = '0') {
    let originalValue;
    try {
      switch (Number(divider)) {
        case 18:
        case 9:
        case 6: {
          originalValue = formatUnits(BigInt(value), Number(divider));
          break;
        }
        default: {
          originalValue = Number(value) / Math.pow(10, (18 - divider));
          break;
        }
      }
      return originalValue;
    }
    catch (e) {
      console.log('conversion exception..', e);
    }
    finally {
      return originalValue;
    }
  }

}
